import {UIGameManager} from '../UIGameManager'
import {UIGameBarbarianHexView} from './UIGameBarbarianHexView'
import {UIGameSpriteWithNotificationView} from './UIGameSpriteWithNotificationView'
import {TranslationObject} from '@colonist/i18n'
import {Sprites} from '../UIGameImageDictionary'
import {UIGameSpriteHandler} from '../../UIGameSpriteHandler'
import {SectionBgView} from '../../ui_canvas_engine/SectionBgView'
import {UIUtilsEjsVariables} from '../../UIUtilsEjsVariables'
import {MechanicBarbarianInvasionState, MechanicKnightState} from '@colonist/game-states'
import {MechanicValidatorBarbarianInvasion, MechanicValidatorKnight} from '@colonist/game-mechanic-validators'

export class UIGameBarbarianInvasionProgressTrackerView extends SectionBgView {
    protected readonly uiGameManager: UIGameManager
    private readonly barbarianInvasionValidator: MechanicValidatorBarbarianInvasion
    private readonly knightValidator: MechanicValidatorKnight
    private readonly hexagonViews: UIGameBarbarianHexView[]
    private currentActiveHex: UIGameBarbarianHexView
    private readonly barbarianShipView: UIGameSpriteWithNotificationView
    private readonly knightStrengthView: UIGameSpriteWithNotificationView

    constructor(uiGameManager: UIGameManager, barbarianInvasionState: MechanicBarbarianInvasionState, knightState: MechanicKnightState) {
        const canvas = uiGameManager.canvasController
        const w = canvas.itemSize * 0.85
        const userOnMobileApp = UIUtilsEjsVariables.isUserOnMobileApp && UIUtilsEjsVariables.shouldDisplayNewMobileGameUI
        const x = userOnMobileApp ? canvas.itemSize * 0.1 : 0
        const hexagonWidth = w * 2 / 3
        const hexagonHeightToWidthRatio = Math.sqrt(3) / 2
        const hexagonHeight = hexagonWidth * hexagonHeightToWidthRatio
        const totalHexagons = 8
        const paddingY = hexagonHeight * 0.18
        const verticalHexagonPackingFactor = 3 / 4
        const containerHeightInHexagons = verticalHexagonPackingFactor * totalHexagons + 1 / 2
        const h = containerHeightInHexagons * (hexagonHeight + paddingY) + paddingY / 2
        const y = userOnMobileApp ? canvas.appH - h - canvas.itemSize * 4.8 : canvas.itemSize * 2.6
        super(x, y, w, h, canvas.app.stage)

        this.uiGameManager = uiGameManager
        this.barbarianInvasionValidator = new MechanicValidatorBarbarianInvasion(this.uiGameManager.gameController.getPlayerValidators(), this.uiGameManager.gameController.mapValidator, barbarianInvasionState)
        this.knightValidator = new MechanicValidatorKnight(this.uiGameManager.gameController.getPlayerValidators(), this.uiGameManager.gameController.mapValidator, knightState)
        this.hexagonViews = this.constructHexagonViews(w)

        this.currentActiveHex = this.hexagonViews[0]
        this.currentActiveHex.setActiveTexture()
        this.setLastHexToAttackTexture()

        const iconSize = hexagonWidth

        const barbarianTexture = UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.icon_barbarian_ship)
        const barbarianShipPoint = this.currentActiveHex.getPointForBarbarianShip()
        this.barbarianShipView = new UIGameSpriteWithNotificationView(barbarianShipPoint.x, barbarianShipPoint.y, iconSize, iconSize, this, barbarianTexture)
        this.barbarianShipView.counter.setNeutralNotificationTexture()

        const barbarianShipTipTitle: TranslationObject = {key: 'strings:game.tips.barbarians.title'}
        const barbarianShipTipBody: TranslationObject[] = [{key: 'strings:game.tips.barbarians.body.0'}, {key: 'strings:game.tips.barbarians.body.1'}]
        this.barbarianShipView.spriteView.setTip(barbarianShipTipTitle, barbarianShipTipBody)

        const knightSwordTexture = UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.icon_swords)
        const knightStrengthPoint = this.currentActiveHex.getPointForKnightSwords()
        this.knightStrengthView = new UIGameSpriteWithNotificationView(knightStrengthPoint.x, knightStrengthPoint.y, iconSize, iconSize, this, knightSwordTexture)

        const knightStrengthTipTitle: TranslationObject = {key: 'strings:game.tips.knight.title'}
        const knightStrengthTipBody: TranslationObject[] = [{key: 'strings:game.tips.knight.body.0'}, {key: 'strings:game.tips.knight.body.1'}]
        this.knightStrengthView.spriteView.setTip(knightStrengthTipTitle, knightStrengthTipBody)
    }

    private constructHexagonViews(w: number): UIGameBarbarianHexView[] {
        const hexagonWidth = w * 2 / 3
        const paddingX = hexagonWidth * 0.05
        const hexagonHeightToWidthRatio = Math.sqrt(3) / 2
        const hexagonHeight = hexagonWidth * hexagonHeightToWidthRatio
        const paddingY = hexagonHeight * 0.18
        const hexagonViews: UIGameBarbarianHexView[] = []
        let hexagonX = 0
        let hexagonY = 0

        for(let hexagonCount = 0; hexagonCount < 8; hexagonCount += 1) {
            hexagonViews.push(new UIGameBarbarianHexView(hexagonCount, hexagonX, hexagonY, hexagonWidth, hexagonWidth, this))
            hexagonX = (hexagonCount + 1) % 2 * (hexagonWidth / 2 - paddingX)
            hexagonY += hexagonHeight * 3 / 4 + paddingY
        }

        return hexagonViews
    }

    private setLastHexToAttackTexture() {
        const lastHex = this.hexagonViews[this.hexagonViews.length - 1]
        lastHex.setAttackTexture()
    }

    private getHexagonForBarbarianStrength(barbarianProgress: number): UIGameBarbarianHexView | undefined {
        for(const hexagonView of this.hexagonViews) {
            if(hexagonView.barbarianProgressMarker == barbarianProgress) return hexagonView
        }
        return undefined
    }

    updateProgressTracker() {
        const newActiveHex = this.getHexagonForBarbarianStrength(this.barbarianInvasionValidator.getBarbarianProgress())
        if(newActiveHex == undefined) return

        const barbarianStrength = this.barbarianInvasionValidator.getBarbarianStrength()
        const barbarianShipPoint = newActiveHex.getPointForBarbarianShip()
        const barbarianShipSpeed = 0.5
        this.barbarianShipView.animationController.animateTo(barbarianShipPoint, barbarianShipSpeed)
        this.barbarianShipView.counter.updateCount(barbarianStrength)

        const knightStrength = this.knightValidator.getTotalKnightStrength()
        const knightStrengthPoint = newActiveHex.getPointForKnightSwords()
        const knightStrengthSpeed = 0.5
        this.knightStrengthView.animationController.animateTo(knightStrengthPoint, knightStrengthSpeed)
        this.knightStrengthView.counter.updateCount(knightStrength)
        const arePlayersWinning = knightStrength >= barbarianStrength
        arePlayersWinning ? this.knightStrengthView.counter.setWinningNotificationTexture() : this.knightStrengthView.counter.setLosingNotificationTexture()

        newActiveHex.setActiveTexture()
        this.setLastHexToAttackTexture()
        if(newActiveHex.barbarianProgressMarker == this.currentActiveHex.barbarianProgressMarker) return
        this.currentActiveHex.setInactiveTexture()
        this.currentActiveHex = newActiveHex
    }
}
